/* ============================================================
   VIORAH – Main JavaScript
   ============================================================ */
'use strict';

/* ---- Utility ---- */
const qs  = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* ============================================================
   VARIANT MANAGER
   Handles pack + colour selection → updates variant ID + price
   ============================================================ */
class ViorahVariantManager {
  constructor(form) {
    this.form      = form;
    this.variants  = window.ViorahVariants || [];
    this.options   = window.ViorahProduct?.options || [];
    this.selected  = {
      menge: null,
      farbe:  null
    };
    this.variantInput = qs('#variantId', form);
    this.priceEl      = qs('#productPrice', form.closest('.product-info') || document);
    this.btnAdd       = qs('#btnAddToCart', form);

    this._initDefaults();
    this._bindEvents();
  }

  _initDefaults() {
    const selected = qs('.pack-option.selected', this.form.closest('.product-info') || document);
    if (selected) this.selected.menge = selected.dataset.menge;

    const selColor = qs('.color-pick.selected', this.form.closest('.product-info') || document);
    if (selColor) this.selected.farbe = selColor.dataset.farbe;
  }

  _findVariant() {
    if (!this.variants.length) return null;
    return this.variants.find(v =>
      (!this.selected.menge || v.option1 === this.selected.menge) &&
      (!this.selected.farbe  || v.option2 === this.selected.farbe)
    ) || this.variants[0];
  }

  _updateUI(variant) {
    if (!variant) return;
    if (this.variantInput) this.variantInput.value = variant.id;

    if (this.priceEl) {
      const price = (variant.price / 100).toFixed(2).replace('.', '.');
      this.priceEl.textContent = `CHF ${price}`;
      this.priceEl.dataset.price = variant.price;
    }

    if (this.btnAdd) {
      if (!variant.available) {
        this.btnAdd.disabled = true;
        this.btnAdd.textContent = 'Ausverkauft';
      } else {
        this.btnAdd.disabled = false;
        this.btnAdd.textContent = 'In den Warenkorb';
      }
    }
  }

  selectMenge(value) {
    this.selected.menge = value;
    this._updateUI(this._findVariant());
  }

  selectFarbe(value) {
    this.selected.farbe = value;
    this._updateUI(this._findVariant());
  }

  _bindEvents() {
    qsa('.pack-option').forEach(opt => {
      opt.addEventListener('click', () => {
        qsa('.pack-option').forEach(o => o.classList.remove('selected'));
        opt.classList.add('selected');
        this.selectMenge(opt.dataset.menge);
      });
    });

    qsa('.color-pick').forEach(pick => {
      pick.addEventListener('click', () => {
        qsa('.color-pick').forEach(p => p.classList.remove('selected'));
        pick.classList.add('selected');
        const nameEl = qs('#colorName');
        if (nameEl) nameEl.textContent = pick.dataset.farbe || pick.dataset.color;
        this.selectFarbe(pick.dataset.farbe || pick.dataset.color);
      });
    });
  }
}

/* ============================================================
   CART AJAX
   ============================================================ */
class ViorahCart {
  static async addItem(formData, properties) {
    const routes = window.Viorah?.routes || {};
    const body = { id: formData.get('id'), quantity: 1 };
    if (properties && Object.keys(properties).length) body.properties = properties;
    const res = await fetch(routes.cart_add || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }

  static updateCount(n) {
    qsa('.cart-count').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? 'flex' : 'none';
    });
  }

  static async changeItem(key, quantity) {
    const routes = window.Viorah?.routes || {};
    const res = await fetch(routes.cart_change || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }
}

/* ============================================================
   PRODUCT FORM – ADD TO CART
   ============================================================ */
function initProductForm() {
  const form = qs('#ProductForm');

  function findVariant(mengeVal, colorName) {
    const variants = window.ViorahVariants || [];
    return variants.find(v =>
      (v.option1 === colorName || v.option2 === colorName) &&
      (v.option1 === mengeVal  || v.option2 === mengeVal)
    ) || null;
  }

  function setPropertyInput(name, value) {
    if (!form) return;
    const key = `properties[${name}]`;
    let inp = form.querySelector(`input[name="${key}"]`);
    if (!inp) {
      inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = key;
      form.appendChild(inp);
    }
    inp.value = value;
  }

  function syncProperties() {
    if (!form) return;
    form.querySelectorAll('input[name^="properties["]').forEach(el => el.remove());
    const pack = qs('.pack-option.selected');
    if (!pack) return;
    const menge = parseInt(pack.dataset.menge, 10) || 1;
    setPropertyInput('Stückzahl', String(menge));
    pack.querySelectorAll('.color-drop-row').forEach(row => {
      const num   = row.querySelector('.color-drop-num')?.textContent?.replace('#','').trim() || '';
      const label = row.querySelector('.cd-label')?.textContent?.trim() || 'Schwarz';
      if (num) setPropertyInput(`Farbe ${num}`, label);
    });
  }

  const defaultPack = qs('.pack-option.selected');
  if (defaultPack && form) {
    const vi = qs('#variantId');
    if (vi && defaultPack.dataset.variantId) vi.value = defaultPack.dataset.variantId;
  }
  syncProperties();

  qsa('.pack-option').forEach(opt => {
    opt.addEventListener('click', e => {
      if (e.target.closest('.color-drop')) return;
      qsa('.pack-option').forEach(o => o.classList.remove('selected'));
      opt.classList.add('selected');
      const vi = qs('#variantId');
      if (vi) {
        const firstLabel = opt.querySelector('.color-drop-row .cd-label')?.textContent?.trim();
        const match = firstLabel ? findVariant(opt.dataset.menge, firstLabel) : null;
        const newId = (match ? String(match.id) : null) || opt.dataset.variantId;
        if (newId) vi.value = newId;
      }
      syncProperties();
    });
  });

  qsa('.color-drop').forEach(drop => {
    const trigger = qs('.color-drop-trigger', drop);
    const menu    = qs('.color-drop-menu', drop);
    if (!trigger || !menu) return;
    trigger.addEventListener('click', e => {
      e.stopPropagation();
      qsa('.color-drop.open').forEach(d => { if (d !== drop) d.classList.remove('open'); });
      drop.classList.toggle('open');
    });
    qsa('.color-drop-item', drop).forEach(item => {
      item.addEventListener('click', e => {
        e.stopPropagation();
        qs('.cd-dot', trigger).style.background = item.dataset.bg;
        qs('.cd-label', trigger).textContent     = item.dataset.name;
        qsa('.color-drop-item', drop).forEach(i => i.classList.remove('active'));
        item.classList.add('active');
        drop.classList.remove('open');
        const pack = drop.closest('.pack-option');
        if (pack && pack.classList.contains('selected')) {
          const rows = pack.querySelectorAll('.color-drop-row');
          if (rows[0] === drop.closest('.color-drop-row')) {
            const match = findVariant(pack.dataset.menge, item.dataset.name);
            const vi = qs('#variantId');
            if (vi && match) vi.value = String(match.id);
          }
        }
        syncProperties();
      });
    });
  });

  document.addEventListener('click', () => qsa('.color-drop.open').forEach(d => d.classList.remove('open')));

  /* ── ADD TO CART ── */
  const addBtn = document.getElementById('btnAddToCart');
  if (addBtn && form) {
    addBtn.addEventListener('click', async function(e) {
      e.preventDefault();
      const btn  = this;
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = '…';

      try {
        const pack      = document.querySelector('.pack-option.selected');
        const menge     = parseInt(pack?.dataset?.menge, 10) || 1;
        const variantId = parseInt(qs('#variantId')?.value || pack?.dataset?.variantId || '0', 10);

        if (!variantId) throw new Error('Keine Variante gefunden');

        const properties = { Stueckzahl: String(menge) };
        if (pack) {
          pack.querySelectorAll('.color-drop-row').forEach(function(row, idx) {
            const label = row.querySelector('.cd-label')?.textContent?.trim() || 'Schwarz';
            properties['Farbe ' + (idx + 1)] = label;
          });
        }

        console.log('[Viorah] Cart add – variant ID:', variantId, '| pack:', pack?.dataset?.variantId);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, properties })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || 'Fehler ' + res.status);
        }

        btn.textContent = '✓ Hinzugefügt!';
        btn.style.background = '#5B8C7A';

        const cartRes  = await fetch('/cart.js');
        const cartData = await cartRes.json();
        ViorahCart.updateCount(cartData.item_count);

        const modal = document.getElementById('cartModal');
        if (modal) {
          modal.classList.add('open');
          const closeModal = () => {
            modal.classList.remove('open');
            btn.textContent = orig;
            btn.style.background = '';
            btn.disabled = false;
          };
          const continueBtn = modal.querySelector('.cart-modal-btn-continue');
          if (continueBtn) continueBtn.onclick = closeModal;
          modal.addEventListener('click', ev => { if (ev.target === modal) closeModal(); }, { once: true });
        } else {
          setTimeout(() => { btn.textContent = orig; btn.style.background = ''; btn.disabled = false; }, 2400);
        }

      } catch (err) {
        console.error('[Viorah] Cart error:', err);
        btn.textContent = err.message || 'Fehler';
        setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 4000);
      }
    });
  }
}

/* ============================================================
   PURCHASE TOGGLE (One-time / Subscribe)
   ============================================================ */
function initPurchaseToggle() {
  const btnOnetime   = qs('#btnOnetime');
  const btnSubscribe = qs('#btnSubscribe');
  if (!btnOnetime || !btnSubscribe) return;
  btnOnetime.addEventListener('click', () => {
    btnOnetime.classList.add('active');
    btnSubscribe.classList.remove('active');
  });
  btnSubscribe.addEventListener('click', () => {
    btnSubscribe.classList.add('active');
    btnOnetime.classList.remove('active');
  });
}

/* ============================================================
   COUNTDOWN TIMER
   ============================================================ */
function initTimer() {
  const hEl = qs('#timerH'), mEl = qs('#timerM'), sEl = qs('#timerS');
  if (!hEl) return;
  function tick() {
    const now  = new Date();
    const end  = new Date(now); end.setHours(23, 59, 59, 0);
    const diff = Math.max(0, Math.floor((end - now) / 1000));
    hEl.textContent = String(Math.floor(diff / 3600)).padStart(2, '0');
    mEl.textContent = String(Math.floor(diff % 3600 / 60)).padStart(2, '0');
    sEl.textContent = String(diff % 60).padStart(2, '0');
  }
  tick();
  setInterval(tick, 1000);
}

/* ============================================================
   GALLERY THUMBNAILS
   ============================================================ */
function initGallery() {
  const thumbs   = qsa('.thumb');
  const mainWrap = qs('#GalleryMain') || qs('.gallery-main');
  const mainImg  = qs('#GalleryMainImg');
  const prevBtn  = qs('#GalleryPrev');
  const nextBtn  = qs('#GalleryNext');

  function goTo(idx) {
    const clamped = (idx + thumbs.length) % thumbs.length;
    const thumb   = thumbs[clamped];
    if (!thumb) return;
    thumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    let url = thumb.dataset.imgUrl;
    if (!url) {
      const tImg = thumb.querySelector('img');
      if (tImg) url = tImg.src.replace(/width=\d+/, 'width=900');
    }
    if (url && mainImg) {
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.setAttribute('src', url);
    }
  }

  function activeIdx() { return thumbs.findIndex(t => t.classList.contains('active')); }

  thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(activeIdx() - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(activeIdx() + 1));

  const thumbsTrack = qs('#GalleryThumbs');
  const tPrev = qs('.gallery-thumbs-prev');
  const tNext = qs('.gallery-thumbs-next');
  if (thumbsTrack && tPrev) tPrev.addEventListener('click', () => thumbsTrack.scrollBy({ left: -180, behavior: 'smooth' }));
  if (thumbsTrack && tNext) tNext.addEventListener('click', () => thumbsTrack.scrollBy({ left: 180, behavior: 'smooth' }));

  if (thumbs.length <= 1) {
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
  }

  if (mainWrap) {
    let startX = 0;
    let startY = 0;
    mainWrap.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    mainWrap.addEventListener('touchmove', e => {
      const dx = Math.abs(e.touches[0].clientX - startX);
      const dy = Math.abs(e.touches[0].clientY - startY);
      if (dx > dy && dx > 8) e.preventDefault();
    }, { passive: false });
    mainWrap.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 20) return;
      goTo(diff > 0 ? activeIdx() + 1 : activeIdx() - 1);
    }, { passive: true });
  }
}

/* ============================================================
   FAQ ACCORDION
   ============================================================ */
function initFAQ() {
  qsa('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item   = btn.closest('.faq-item');
      const isOpen = item.classList.contains('open');
      qsa('.faq-item').forEach(i => i.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });
}

/* ============================================================
   SCROLL REVEAL
   ============================================================ */
function initReveal() {
  if (!window.IntersectionObserver) {
    qsa('.reveal').forEach(el => el.classList.add('visible'));
    return;
  }
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
    });
  }, { threshold: 0.08 });
  qsa('.reveal').forEach(el => obs.observe(el));
}

/* ============================================================
   STAT RINGS (animated SVG circles)
   ============================================================ */
function initStatRings() {
  if (!window.IntersectionObserver) {
    qsa('.stat-fill').forEach(c => { c.style.strokeDashoffset = c.dataset.target; });
    return;
  }
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.style.strokeDashoffset = e.target.dataset.target;
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0.4 });
  qsa('.stat-fill').forEach(el => obs.observe(el));
}

/* ============================================================
   LENA CHAT WIDGET
   ============================================================ */
function initChat() {
  const widget   = qs('#chatWidget');
  const toggle   = qs('#chatToggle');
  const messages = qs('#chatMessages');
  const input    = qs('#chatInput');
  const send     = qs('#chatSend');
  const chips    = qs('#chatChips');
  if (!widget || !toggle) return;

  toggle.addEventListener('click', () => {
    widget.classList.toggle('open');
    if (widget.classList.contains('open') && input) input.focus();
  });

  const replies = [
    'Das ist eine ausgezeichnete Frage! Das Viorah Handtuch eignet sich besonders für empfindliche und reife Haut. Darf ich Ihnen mehr über die Inhaltsstoffe erzählen?',
    'Unsere Kundinnen und Kunden berichten von sichtbaren Ergebnissen bereits nach 7–14 Tagen. Kann ich Ihnen beim Auswählen des richtigen Pakets helfen?',
    'Beim 2er Pack erhalten Sie ein Handtuch gratis dazu – das ist unsere beliebteste Wahl! Möchten Sie es direkt bestellen?',
    'Bei weiteren Fragen stehe ich gerne zur Verfügung – oder wenden Sie sich an support@viorah.ch. Gibt es noch etwas, womit ich Ihnen helfen kann?',
    'Das Handtuch ist mit Bambus-Kohlefasern hergestellt und antibakteriell durch Silberionen – frei von Chemikalien, ideal für den täglichen Gebrauch. Darf ich mehr erzählen?',
  ];
  let replyIdx = 0;

  function appendMsg(text, type) {
    const div = document.createElement('div');
    div.className = `msg ${type}`;
    div.textContent = text;
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
  }

  function sendMessage(text) {
    text = text.trim();
    if (!text) return;
    appendMsg(text, 'user');
    if (input) input.value = '';

    const typing = document.createElement('div');
    typing.className = 'typing-indicator';
    typing.innerHTML = '<div class="typing-dot"></div><div class="typing-dot"></div><div class="typing-dot"></div>';
    messages.appendChild(typing);
    messages.scrollTop = messages.scrollHeight;

    setTimeout(() => {
      typing.remove();
      appendMsg(replies[replyIdx % replies.length], 'bot');
      replyIdx++;
    }, 900 + Math.random() * 500);
  }

  if (send)  send.addEventListener('click', () => sendMessage(input.value));
  if (input) input.addEventListener('keydown', e => { if (e.key === 'Enter') sendMessage(input.value); });

  if (chips) {
    chips.addEventListener('click', e => {
      const chip = e.target.closest('.chip');
      if (!chip) return;
      sendMessage(chip.textContent);
      chips.style.display = 'none';
    });
  }
}

/* ============================================================
   CART PAGE (quantity / remove)
   ============================================================ */
function initCartPage() {
  const form = qs('#CartForm');
  if (!form) return;

  async function setQuantity(key, quantity) {
    form.style.opacity = '.5';
    try {
      const cart = await ViorahCart.changeItem(key, quantity);
      ViorahCart.updateCount(cart.item_count);
      window.location.reload();
    } catch {
      form.style.opacity = '';
    }
  }

  form.addEventListener('click', e => {
    const minus  = e.target.closest('.cr-qty-minus');
    const plus   = e.target.closest('.cr-qty-plus');
    const remove = e.target.closest('.cr-remove');

    if (minus || plus) {
      const row   = e.target.closest('.cart-row');
      const input = qs('.cr-qty-input', row);
      const next  = Math.max(0, (parseInt(input.value, 10) || 0) + (plus ? 1 : -1));
      setQuantity(row.dataset.key, next);
    }

    if (remove) {
      setQuantity(remove.dataset.key, 0);
    }
  });

  form.addEventListener('change', e => {
    if (!e.target.classList.contains('cr-qty-input')) return;
    const next = Math.max(0, parseInt(e.target.value, 10) || 0);
    setQuantity(e.target.dataset.key, next);
  });
}

/* ============================================================
   CART COUNT INIT
   ============================================================ */
function initCartCount() {
  const count = window.Viorah?.cartCount || 0;
  ViorahCart.updateCount(count);
}

/* ============================================================
   INIT ALL
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initProductForm();
  initPurchaseToggle();
  initTimer();
  initGallery();
  initFAQ();
  initReveal();
  initStatRings();
  initChat();
  initCartCount();
  initCartPage();
});